import React, { useCallback, useEffect, useState, useRef, Fragment } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, useMap, useMapEvents, ZoomControl } from 'react-leaflet';
import L from 'leaflet';
import { JobHoverTooltip } from './JobHoverTooltip';
interface Job {
  id: number;
  timeStart: string;
  timeEnd: string;
  address: string;
  city: string;
  host: string;
  type: 'Pulizia Standard' | 'Pulizia Profonda' | 'Check-out';
  cleaner: {
    name: string;
    avatarColor: string;
    initials: string;
  };
  status: 'scheduled' | 'in-progress' | 'completed' | 'issue';
  earnings: string;
  lat: number;
  lng: number;
}
interface TeamMember {
  id: string;
  name: string;
  initials: string;
  color: string;
  textColor: string;
  currentLat: number;
  currentLng: number;
}
interface JobsMapProps {
  jobs: Job[];
  activeJobId: number | null;
  onJobSelect: (id: number) => void;
  teamMembers: TeamMember[];
  selectedMemberId: string | null;
  onMemberSelect: (id: string | null) => void;
}
// --- Map Helpers ---
function MapResizer() {
  const map = useMap();
  useEffect(() => {
    const timer = setTimeout(() => map.invalidateSize(), 200);
    const handleResize = () => map.invalidateSize();
    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, [map]);
  return null;
}
function ZoomTracker({
  onZoomChange
}: {
  onZoomChange: (zoom: number) => void;
}) {
  const map = useMapEvents({
    zoomend: () => onZoomChange(map.getZoom())
  });
  useEffect(() => onZoomChange(map.getZoom()), []);
  return null;
}
function MapController({
  center,
  zoom
}: {
  center: [number, number];
  zoom: number;
}) {
  const map = useMap();
  const isFirst = useRef(true);
  const prevCenter = useRef(center);
  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      map.setView(center, zoom);
      prevCenter.current = center;
      return;
    }
    // If center changed, pan smoothly without zoom animation
    if (prevCenter.current[0] !== center[0] || prevCenter.current[1] !== center[1]) {
      map.flyTo(center, zoom, {
        animate: true,
        duration: 1.5
      });
    }
    prevCenter.current = center;
  }, [center[0], center[1], zoom]);
  return null;
}
function TooltipPositionUpdater({
  hoveredJob,
  onPositionUpdate
}: {
  hoveredJob: Job | null;
  onPositionUpdate: (pos: {
    x: number;
    y: number;
  }) => void;
}) {
  const map = useMap();
  const updatePosition = useCallback(() => {
    if (hoveredJob) {
      const point = map.latLngToContainerPoint([hoveredJob.lat, hoveredJob.lng]);
      onPositionUpdate({
        x: point.x,
        y: point.y
      });
    }
  }, [map, hoveredJob, onPositionUpdate]);
  useEffect(() => updatePosition(), [updatePosition]);
  useMapEvents({
    move: updatePosition,
    zoom: updatePosition
  });
  return null;
}
// Deselect team member on empty map click
function MapClickDeselect({
  onDeselect
}: {
  onDeselect: () => void;
}) {
  useMapEvents({
    click: () => {
      onDeselect();
    }
  });
  return null;
}
// --- Icon Factories ---
function createHeadquarterIcon(): L.DivIcon {
  const size = 36;
  const html = `
    <div style="position: relative; width: ${size}px; height: ${size}px;">
      <div style="
        width: ${size}px; height: ${size}px;
        background: #1E365C; color: #ffffff;
        border: 2px solid #ffffff;
        border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        box-shadow: 0 4px 12px rgba(30,54,92,0.2);
      ">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect>
          <path d="M9 22v-4h6v4"></path>
          <path d="M8 6h.01"></path>
          <path d="M16 6h.01"></path>
          <path d="M8 10h.01"></path>
          <path d="M16 10h.01"></path>
          <path d="M8 14h.01"></path>
          <path d="M16 14h.01"></path>
        </svg>
      </div>
    </div>
  `;
  return L.divIcon({
    html,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
}
function createJobMarkerIcon(status: Job['status'], isActive: boolean): L.DivIcon {
  const size = isActive ? 38 : 32;
  // Status determines icon color only — background stays white
  let iconColor: string;
  let iconSvg: string;
  switch (status) {
    case 'in-progress':
      iconColor = '#3B82F6'; // blue
      iconSvg = '<polygon points="10 7 17 12 10 17" fill="${c}" stroke="${c}" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>';
      break;
    case 'completed':
      iconColor = '#22C55E'; // green
      iconSvg = '<polyline points="6 12 10 16 18 8" stroke="${c}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>';
      break;
    case 'issue':
      iconColor = '#F59E0B'; // amber
      iconSvg = '<line x1="12" y1="8" x2="12" y2="13" stroke="${c}" stroke-width="2.5" stroke-linecap="round"/><circle cx="12" cy="17" r="1" fill="${c}"/>';
      break;
    default:
      // scheduled
      iconColor = '#6B7280'; // gray
      iconSvg = '<circle cx="12" cy="12" r="7" stroke="${c}" stroke-width="2" fill="none"/><polyline points="12 9 12 12 14.5 13.5" stroke="${c}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>';
  }
  iconSvg = iconSvg.replace(/\$\{c\}/g, iconColor);
  // Border always matches icon color; thicker when active
  const borderColor = iconColor;
  const borderWidth = isActive ? '3.5px' : '2.5px';
  const shadow = isActive ? '0 2px 8px rgba(0,0,0,0.15)' : `0 2px 6px ${iconColor}33`;
  const html = `
    <div style="position: relative; width: ${size}px; height: ${size}px;">
      <div style="
        position: relative;
        width: ${size}px; height: ${size}px;
        background: #ffffff;
        border: ${borderWidth} solid ${borderColor};
        border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        box-shadow: ${shadow};
        cursor: pointer;
        transition: all 0.2s ease;
      ">
        <svg width="${size * 0.48}" height="${size * 0.48}" viewBox="0 0 24 24" fill="none">
          ${iconSvg}
        </svg>
      </div>
    </div>
  `;
  return L.divIcon({
    html,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
}
// Team Member Marker Icon
function createTeamMemberIcon(member: TeamMember, isSelected: boolean): L.DivIcon {
  const size = isSelected ? 42 : 36;
  const firstName = member.name.split(' ')[0];
  const bgColor = '#1E365C';
  const border = isSelected ? '3px solid #ffffff' : '2.5px solid #ffffff';
  const shadow = isSelected ? '0 0 0 3px rgba(46, 140, 126, 0.35), 0 4px 12px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.18)';
  // Lucide User icon SVG (person silhouette)
  const userIconSvg = `
    <svg width="${size * 0.48}" height="${size * 0.48}" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
      <circle cx="12" cy="7" r="4"></circle>
    </svg>
  `;
  // Name label — white bg, navy border & text, closer to icon
  const labelBorder = isSelected ? '#2E8C7E' : '#1E365C';
  const labelText = isSelected ? '#2E8C7E' : '#1E365C';
  const nameLabel = `
    <div style="
      position: absolute;
      bottom: -4px;
      left: 50%;
      transform: translateX(-50%);
      background: #ffffff;
      color: ${labelText};
      border: 1.5px solid ${labelBorder};
      padding: 1px 6px;
      border-radius: 999px;
      font-size: 9px;
      font-weight: 600;
      font-family: Inter, sans-serif;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
      white-space: nowrap;
      line-height: 14px;
    ">${firstName}</div>
  `;
  const totalHeight = size + 14;
  const html = `
    <div style="position: relative; width: ${size}px; height: ${totalHeight}px;">
      <div style="
        width: ${size}px; height: ${size}px;
        background: ${bgColor};
        border: ${border};
        border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        box-shadow: ${shadow};
        transition: all 0.2s ease;
      ">
        ${userIconSvg}
      </div>
      ${nameLabel}
    </div>
  `;
  return L.divIcon({
    html,
    className: '',
    iconSize: [size, totalHeight],
    iconAnchor: [size / 2, size / 2]
  });
}
// --- Geo Helpers ---
function haversineDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371; // km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
function estimateTravelMinutes(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const km = haversineDistance(lat1, lng1, lat2, lng2);
  // Straight-line distance with routing factor ~1.4x, city speed ~15 km/h with traffic
  // Effective straight-line speed ≈ 7 km/h
  return Math.max(2, Math.round(km / 7 * 60));
}
function midpoint(lat1: number, lng1: number, lat2: number, lng2: number): [number, number] {
  return [(lat1 + lat2) / 2, (lng1 + lng2) / 2];
}
function bearing(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const y = Math.sin(dLng) * Math.cos(lat2 * Math.PI / 180);
  const x = Math.cos(lat1 * Math.PI / 180) * Math.sin(lat2 * Math.PI / 180) - Math.sin(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.cos(dLng);
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
}
function getJobDurationLabel(timeStart: string, timeEnd: string): string {
  const [sh, sm] = timeStart.split(':').map(Number);
  const [eh, em] = timeEnd.split(':').map(Number);
  const mins = eh * 60 + em - (sh * 60 + sm);
  if (mins >= 60) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `${h}h${m.toString().padStart(2, '0')}` : `${h}h`;
  }
  return `${mins}min`;
}
// --- Route Segment Label Icon ---
function createRouteSegmentIcon(travelMin: number, angleDeg: number, isPast: boolean): L.DivIcon {
  const label = travelMin < 60 ? `${travelMin} min` : `${Math.round(travelMin / 60 * 10) / 10}h`;
  const opacity = isPast ? 0.45 : 0.9;
  const html = `
    <div style="
      display: inline-flex; align-items: center; gap: 4px;
      background: #ffffff;
      border: 1.5px solid #1E365C;
      border-radius: 999px;
      padding: 3px 10px;
      font-size: 10px;
      font-weight: 700;
      font-family: Inter, sans-serif;
      color: #1E365C;
      box-shadow: 0 2px 6px rgba(0,0,0,0.12);
      white-space: nowrap;
      opacity: ${opacity};
      line-height: 1;
      letter-spacing: -0.2px;
    ">
      <span style="font-size: 11px; line-height: 1;">🚗</span>
      <span>${label}</span>
    </div>
  `;
  return L.divIcon({
    html,
    className: '',
    iconSize: [80, 22],
    iconAnchor: [40, 11]
  });
}
// --- Cleaning Duration Badge Icon ---
function createDurationBadgeIcon(duration: string): L.DivIcon {
  const html = `
    <div style="
      display: inline-flex; align-items: center; gap: 3px;
      background: #ffffff;
      border: 1.5px solid #2E8C7E;
      border-radius: 999px;
      padding: 2px 8px;
      font-size: 10px;
      font-weight: 700;
      font-family: Inter, sans-serif;
      color: #2E8C7E;
      box-shadow: 0 2px 6px rgba(0,0,0,0.12);
      white-space: nowrap;
      line-height: 1;
    ">
      <span style="font-size: 10px; line-height: 1;">🕐</span>
      <span>${duration}</span>
    </div>
  `;
  return L.divIcon({
    html,
    className: '',
    iconSize: [64, 20],
    iconAnchor: [32, 10] // Center the badge at the offset position
  });
}
// --- Arrowhead Icon for route segments ---
function createArrowheadIcon(angleDeg: number, isPast: boolean): L.DivIcon {
  const opacity = isPast ? 0.35 : 0.75;
  const size = 16;
  // Triangle pointing right, rotated to match bearing
  const html = `
    <div style="
      width: ${size}px; height: ${size}px;
      display: flex; align-items: center; justify-content: center;
      opacity: ${opacity};
    ">
      <svg width="${size}" height="${size}" viewBox="0 0 16 16" style="transform: rotate(${angleDeg}deg);">
        <polygon points="4,2 14,8 4,14" fill="#1E365C" />
      </svg>
    </div>
  `;
  return L.divIcon({
    html,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
}
// Helper: get a point at a fraction along a segment
function interpolate(lat1: number, lng1: number, lat2: number, lng2: number, t: number): [number, number] {
  return [lat1 + (lat2 - lat1) * t, lng1 + (lng2 - lng1) * t];
}
export function JobsMap({
  jobs,
  activeJobId,
  onJobSelect,
  teamMembers,
  selectedMemberId,
  onMemberSelect
}: JobsMapProps) {
  const defaultCenter: [number, number] = [41.9028, 12.4964];
  const [currentZoom, setCurrentZoom] = useState(13);
  const [hoveredJob, setHoveredJob] = useState<Job | null>(null);
  const [tooltipPos, setTooltipPos] = useState({
    x: 0,
    y: 0
  });
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const activeJob = jobs.find(j => j.id === activeJobId);
  const center = activeJob ? [activeJob.lat, activeJob.lng] as [number, number] : defaultCenter;
  const targetZoom = activeJob ? 15 : 13;
  const handleMarkerMouseOver = (job: Job) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => setHoveredJob(job), 100);
  };
  const handleMarkerMouseOut = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setHoveredJob(null);
  };
  // Calculate routes for selected member(s)
  const getMemberRoutes = () => {
    const membersToShow = selectedMemberId ? teamMembers.filter(m => m.id === selectedMemberId) : [];
    return membersToShow.map(member => {
      const memberJobs = jobs.filter((j: any) => j.cleanerId === member.id).sort((a, b) => a.timeStart.localeCompare(b.timeStart));
      const completedJobs = memberJobs.filter(j => j.status === 'completed');
      const futureJobs = memberJobs.filter(j => j.status !== 'completed');
      const pastPath: [number, number][] = [];
      completedJobs.forEach(j => pastPath.push([j.lat, j.lng]));
      pastPath.push([member.currentLat, member.currentLng]);
      const futurePath: [number, number][] = [];
      futurePath.push([member.currentLat, member.currentLng]);
      futureJobs.forEach(j => futurePath.push([j.lat, j.lng]));
      return {
        member,
        pastPath,
        futurePath,
        memberJobs
      };
    });
  };
  const routes = getMemberRoutes();
  // Build route segment markers (arrows + travel time)
  const routeSegmentMarkers: {
    position: [number, number];
    icon: L.DivIcon;
    key: string;
  }[] = [];
  // Build arrowhead markers at segment endpoints
  const arrowheadMarkers: {
    position: [number, number];
    icon: L.DivIcon;
    key: string;
  }[] = [];
  routes.forEach(route => {
    // Past segments
    for (let i = 0; i < route.pastPath.length - 1; i++) {
      const [lat1, lng1] = route.pastPath[i];
      const [lat2, lng2] = route.pastPath[i + 1];
      const mid = midpoint(lat1, lng1, lat2, lng2);
      const angle = bearing(lat1, lng1, lat2, lng2);
      const mins = estimateTravelMinutes(lat1, lng1, lat2, lng2);
      routeSegmentMarkers.push({
        position: mid,
        icon: createRouteSegmentIcon(mins, angle, true),
        key: `past-seg-${route.member.id}-${i}`
      });
      // Arrowhead near endpoint (85% along segment)
      const arrowPos = interpolate(lat1, lng1, lat2, lng2, 0.82);
      arrowheadMarkers.push({
        position: arrowPos,
        icon: createArrowheadIcon(angle - 90, true),
        key: `past-arrow-${route.member.id}-${i}`
      });
    }
    // Future segments
    for (let i = 0; i < route.futurePath.length - 1; i++) {
      const [lat1, lng1] = route.futurePath[i];
      const [lat2, lng2] = route.futurePath[i + 1];
      const mid = midpoint(lat1, lng1, lat2, lng2);
      const angle = bearing(lat1, lng1, lat2, lng2);
      const mins = estimateTravelMinutes(lat1, lng1, lat2, lng2);
      routeSegmentMarkers.push({
        position: mid,
        icon: createRouteSegmentIcon(mins, angle, false),
        key: `future-seg-${route.member.id}-${i}`
      });
      // Arrowhead near endpoint (85% along segment)
      const arrowPos = interpolate(lat1, lng1, lat2, lng2, 0.82);
      arrowheadMarkers.push({
        position: arrowPos,
        icon: createArrowheadIcon(angle - 90, false),
        key: `future-arrow-${route.member.id}-${i}`
      });
    }
  });
  // Build cleaning duration markers (only when member selected)
  const durationMarkers: {
    position: [number, number];
    icon: L.DivIcon;
    key: string;
  }[] = [];
  if (selectedMemberId) {
    const selectedMember = teamMembers.find(m => m.id === selectedMemberId);
    const memberJobs = jobs.filter((j: any) => j.cleanerId === selectedMemberId);
    memberJobs.forEach(job => {
      const duration = getJobDurationLabel(job.timeStart, job.timeEnd);
      // Offset the badge position away from the team member to avoid overlap
      // Calculate bearing from team member to job, then offset perpendicular
      let offsetLat = 0;
      let offsetLng = 0;
      if (selectedMember) {
        const angle = bearing(selectedMember.currentLat, selectedMember.currentLng, job.lat, job.lng);
        // Offset perpendicular to the route (90 degrees to the left)
        const perpAngle = (angle + 90) * Math.PI / 180;
        const offsetDist = 0.0008; // ~80m offset in lat/lng
        offsetLat = Math.cos(perpAngle) * offsetDist;
        offsetLng = Math.sin(perpAngle) * offsetDist;
      }
      durationMarkers.push({
        position: [job.lat + offsetLat, job.lng + offsetLng],
        icon: createDurationBadgeIcon(duration),
        key: `duration-${job.id}`
      });
    });
  }
  return <div className="h-full w-full relative overflow-hidden" data-id="element-736">
      <MapContainer center={defaultCenter} zoom={13} scrollWheelZoom={true} zoomControl={false} style={{
      height: '100%',
      width: '100%'
    }} data-id="element-737">
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>' url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" keepBuffer={8} updateWhenZooming={false} updateWhenIdle={true} data-id="element-738" />
        <ZoomControl position="topright" data-id="element-739" />
        <MapResizer data-id="element-740" />
        <ZoomTracker onZoomChange={setCurrentZoom} data-id="element-741" />
        <MapController center={center} zoom={targetZoom} data-id="element-742" />
        <TooltipPositionUpdater hoveredJob={hoveredJob} onPositionUpdate={setTooltipPos} data-id="element-743" />
        <MapClickDeselect onDeselect={() => onMemberSelect(null)} data-id="element-744" />

        {/* Routes */}
        {routes.map(route => <Fragment key={route.member.id} data-id="element-745">
            {/* Past Route (Solid, navy faded) */}
            {route.pastPath.length > 1 && <Polyline positions={route.pastPath} pathOptions={{
          color: '#1E365C',
          weight: 2.5,
          opacity: 0.3,
          dashArray: '1, 0'
        }} data-id="element-746" />}
            {/* Future Route (Dashed, navy) */}
            {route.futurePath.length > 1 && <Polyline positions={route.futurePath} pathOptions={{
          color: '#1E365C',
          weight: 2.5,
          opacity: 0.7,
          dashArray: '8, 6'
        }} data-id="element-747" />}
          </Fragment>)}

        {/* Route segment arrows + travel time labels */}
        {routeSegmentMarkers.map(seg => <Marker key={seg.key} position={seg.position} icon={seg.icon} interactive={false} zIndexOffset={200} data-id="element-748" />)}

        {/* Arrowheads at segment endpoints */}
        {arrowheadMarkers.map(arrow => <Marker key={arrow.key} position={arrow.position} icon={arrow.icon} interactive={false} zIndexOffset={150} data-id="element-749" />)}

        {/* Cleaning duration badges at properties */}
        {durationMarkers.map(dm => <Marker key={dm.key} position={dm.position} icon={dm.icon} interactive={false} zIndexOffset={800} data-id="element-750" />)}

        {/* HQ Marker */}
        <Marker position={[41.8986, 12.4769]} icon={createHeadquarterIcon()} interactive={false} data-id="element-751" />

        {/* Connector lines: team member → in-progress job */}
        {teamMembers.map(member => {
        if (selectedMemberId && selectedMemberId !== member.id) return null;
        // Find in-progress jobs assigned to this member
        const activeJobs = jobs.filter((j: any) => j.cleanerId === member.id && j.status === 'in-progress');
        return activeJobs.map(job => <Polyline key={`connector-${member.id}-${job.id}`} positions={[[member.currentLat, member.currentLng], [job.lat, job.lng]]} pathOptions={{
          color: '#1E365C',
          weight: 2.5,
          opacity: 0.6,
          dashArray: '0'
        }} data-id="element-752" />);
      })}

        {/* Team Member Markers */}
        {teamMembers.map(member => {
        // Only show if no filter or if this is the selected member
        if (selectedMemberId && selectedMemberId !== member.id) return null;
        return <Marker key={member.id} position={[member.currentLat, member.currentLng]} icon={createTeamMemberIcon(member, selectedMemberId === member.id)} eventHandlers={{
          click: e => {
            L.DomEvent.stopPropagation(e.originalEvent);
            onMemberSelect(selectedMemberId === member.id ? null : member.id);
          }
        }} zIndexOffset={1000} // Keep on top of job markers
        data-id="element-753" />;
      })}

        {/* Job Markers */}
        {jobs.map(job => {
        // Filter jobs if a member is selected
        if (selectedMemberId && (job as any).cleanerId !== selectedMemberId) return null;
        return <Marker key={job.id} position={[job.lat, job.lng]} icon={createJobMarkerIcon(job.status, job.id === activeJobId)} zIndexOffset={job.status === 'in-progress' ? 500 : job.id === activeJobId ? 400 : 0} eventHandlers={{
          click: e => {
            L.DomEvent.stopPropagation(e.originalEvent);
            onJobSelect(job.id);
          },
          mouseover: () => handleMarkerMouseOver(job),
          mouseout: handleMarkerMouseOut
        }} data-id="element-754" />;
      })}
      </MapContainer>

      {hoveredJob && <JobHoverTooltip job={hoveredJob} position={tooltipPos} visible={!!hoveredJob} data-id="element-755" />}
    </div>;
}